import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons'
import { VAPermissions } from 'Utils/constants'
import { ListPaymentTypes } from '../ListPaymentType'
import { Formatter } from 'Utils/Formatter'
import ToolTipAmount from 'Components/ToolTipAmount/ToolTipAmount'
import { useTotalMargin } from 'Hooks/useTotalMargin'
import { marginTotal } from 'Utils/functions'
import useValidatePermission from 'Hooks/useValidatePermission'
import YappyPaymentInfo from 'Components/YappyPaymentInfo'
import { getYappyPayments } from 'Utils/yappyPayments'

export const PaymentType = ({ payments, totalPayment, statusPayment }) => {
  const { sellOrderPostData, isCommercialCreditSelected } = useSelector(
    state => state.sellOrder
  )

  const [visibilityCostMargin, setVisibilityCostMargin] = React.useState(true)

  const seeMargenProd = useValidatePermission(VAPermissions.seeMargenProd)
  const { totalPrice, totalCost } = useTotalMargin(
    sellOrderPostData?.Data?.SalesDocumentInfo?.Lines
  )
  const { Amount, Tax, AmountWithTax, Origin, CreditAccountNumber } =
    sellOrderPostData?.Data?.SalesDocumentInfo || {}

  const isWebDocument = Origin === 'AB'
  const showPayments = isWebDocument ? !!payments?.length : !!AmountWithTax
  const creditAccountNumber =
    CreditAccountNumber ||
    isCommercialCreditSelected?.accountItemSelected?.CreditAccountNumber

  // statusPayment llega poblado ("Pendiente") desde que se crea el link de
  // pago, antes de que exista una entrada confirmada en payments — no se
  // puede depender solo del array para detectar un pago Yappy en curso.
  const hasYappyPayment = React.useMemo(
    () => getYappyPayments(payments).length > 0 || Boolean(statusPayment),
    [payments, statusPayment]
  )

  return (
    <div
      style={{ display: 'flex', gap: '16px' }}
      data-testid="container-payment-section">
      <div
        style={{ ...styles.containerStyles, width: '60%' }}
        data-testid="container-payment-methods">
        {hasYappyPayment ? (
          <YappyPaymentInfo
            payments={payments}
            // Mientras el pago está pendiente, PaymentTotal/TotalPayment aún
            // no llegan del backend; el monto solicitado por Yappy siempre
            // es el 100% del total del documento (el monto no es editable
            // al crear el link), así que AmountWithTax es un fallback válido.
            totalPayment={totalPayment ?? AmountWithTax}
            statusPayment={statusPayment}
          />
        ) : (
          showPayments && (
            <>
              <p style={{ fontSize: '14px', fontWeight: '600' }}>
                Métodos de pago
              </p>
              {!isWebDocument && creditAccountNumber && (
                <p style={{ fontSize: '14px' }}>
                  Crédito Comercial {creditAccountNumber}: $
                  {Formatter.Money(AmountWithTax, {
                    rounded: true,
                    decimalPrecision: 2,
                  })}
                </p>
              )}
              {!isWebDocument && !creditAccountNumber && (
                <p style={{ fontSize: '14px' }}>
                  Contado: $
                  {Formatter.Money(AmountWithTax, {
                    rounded: true,
                    decimalPrecision: 2,
                  })}
                </p>
              )}
              <ListPaymentTypes payments={payments} />
            </>
          )
        )}
      </div>
      <div
        style={{ ...styles.containerStyles, width: '40%' }}
        data-testid="container-totals">
        <div style={styles.priceStyles} data-testid="container-subtotal">
          <h3 style={{ fontSize: '14px' }}>Sub Total</h3>
          <p style={{ fontSize: '14px', marginRight: 15 }}>
            ${Formatter.Money(Amount)}
          </p>
        </div>
        <div style={styles.priceStyles} data-testid="container-tax">
          <h3 style={{ fontSize: '14px' }}>ITBMS</h3>
          <ToolTipAmount amount={Tax} />
        </div>
        {seeMargenProd && (
          <div style={styles.priceStyles}>
            <h3 style={{ fontSize: '14px', fontWeight: '600' }}>
              Margen total
            </h3>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
              <span style={{ fontSize: '14px' }}>
                {visibilityCostMargin
                  ? '****'
                  : `${marginTotal(totalCost, totalPrice).toFixed(2)}%`}
              </span>

              <span
                style={{ cursor: 'pointer', fontSize: '14px' }}
                onClick={() => setVisibilityCostMargin(prev => !prev)}>
                <FontAwesomeIcon
                  icon={visibilityCostMargin ? faEyeSlash : faEye}
                />
              </span>
            </div>
          </div>
        )}
        <div style={styles.priceStyles} data-testid="container-total">
          <h3 style={{ fontSize: '14px', fontWeight: '600' }}>Total</h3>
          <ToolTipAmount amount={AmountWithTax} />
        </div>
      </div>
    </div>
  )
}

PaymentType.propTypes = {
  payments: PropTypes.array,
  totalPayment: PropTypes.number,
  statusPayment: PropTypes.string,
}

const styles = {
  containerStyles: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    background: '#FFFFFF',
    borderRadius: '5px',
    marginTop: '15px',
    boxShadow: '0px 1px 4px rgba(21, 34, 50, 0.14)',
    padding: '16px 53px',
    height: 'auto',
  },
  priceStyles: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
  },
}
